import React, { useEffect, useState } from 'react';
import DataTable from 'components/DataTable';
import { Loading } from '../../../components/loading/Loading';
import { tableStyles } from '../../../css/tableStyles';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import { DATE_FORMATS, formatDate, getPageLimit, showError, updatePageLimit } from 'utils';
import { fetchPaginatedAccounts } from 'services/api/accounts';
import qs from 'query-string';
import Filter from './Filter';
import CustomLink from 'components/TableLink';



const columns = [
  {
    name: 'Account ID',
    selector: 'objectId',
    sortable: false,
    // eslint-disable-next-line react/display-name
    cell: row =>
      <CustomLink
        text={row.objectId}
        linkTo={`/accounts/${row.objectId}`}
      />,
  },
  {
    name: 'Created At',
    selector: 'createdAt',
    sortable: false,
    width: '170px',
    format: (row) => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
  },
  {
    name: 'Name',
    selector: 'name',
    sortable: false,
  },
  {
    name: 'Address',
    selector: 'address',
    sortable: false,
  },
  {
    name: 'Phone',
    selector: 'phone',
    sortable: false,
  },
  {
    name: 'Bank Routing Number',
    selector: 'bankRoute',
    sortable: false,
  },
  {
    name: 'Bank Account Number',
    selector: 'bankAccountNo',
    sortable: false,
  },
];

const Accounts = props => {
  let filters: { [p: string]: any; page: number } = {
    page: 0,
  };
  const [loading, setLoading] = useState<boolean>(true);
  const [accounts, setAccounts] = useState<Record<string, any>>({});

  const fetchAccountsFn = async () => {
    setLoading(true);
    const { page, ...rest } = filters;
    try {
      const response = await fetchPaginatedAccounts({
        page,
        limit: getPageLimit(),
        filter: { ...rest },
      });
      setAccounts(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const queryStringToFilter = () => {
    return qs.parse(props.location.search, { parseBooleans: true, parseNumbers: true });
  };

  useEffect(() => {
    filters = {
      ...filters,
      ...queryStringToFilter(),
    };
    fetchAccountsFn();
  }, []);

  const filterToQueryString = () => {
    return qs.stringify({ ...filters });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    updateQueryString();
    fetchAccountsFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const onFilterSubmit = (updatedFilter: Record<string, any>) => {
    if (updatedFilter.accountId) {
      props.history.push(`/accounts/${updatedFilter.accountId}`);
    } else {
      filters = { ...updatedFilter, page: 0 };
      updateQueryString();
      fetchAccountsFn();
    }
  };

  return (
    <div className={`${styles['main-container']}`}>
      <div className={styles['header']}>
        <div className={styles['heading']}>Accounts</div>
        <ActionButton
          text={<>Account <span>+</span></>}
          active={true}
          isLoading={false}
          onClick={() => props.history.push('/accounts/create')}
        />
      </div>
      <Filter {...props} onSubmit={onFilterSubmit} />
      <div className={styles['data-table']}>
        <DataTable
          keyField="objectId"
          columns={columns}
          noHeader={true}
          data={accounts.items}
          highlightOnHover={true}
          customStyles={tableStyles}
          pagination={true}
          paginationServer={true}
          paginationPerPage={getPageLimit()}
          paginationDefaultPage={(filters.page || 0) + 1}
          paginationTotalRows={accounts.total}
          paginationRowsPerPageOptions={[50, 100, 200]}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          loading={loading}
          loadingRowsCount={10}
        />
      </div>
    </div>
  );
};

export { Accounts };
